import { useSelector } from 'react-redux';
import { Check, Copy } from 'lucide-react';
import { Button } from '@design-system';
import type { AppState } from '@client/model/store';
import { selectMaybeClientPartner } from '@client/model/selectors';
import { useEngineDiff, type DiffRow, type PolicyDiffGroup } from '../hooks/useEngineDiff';
import { buildEngineDiffBundle, formatDiffValue } from '../lib/engineDiffBundle';
import { useClipboardCopy } from '../hooks/useClipboardCopy';
import { CollapsibleSection } from './CollapsibleSection';

const selectClientDataOrNull = (state: AppState): Client | null => state.client.data;

function formatValue(row: DiffRow, value: number | undefined): string {
  return formatDiffValue(row.format, value);
}

const ROW_GRID = 'grid grid-cols-[minmax(0,1fr)_8rem_8rem_4rem] items-baseline gap-x-3';

function DiffRows({ rows }: { rows: DiffRow[] }) {
  return (
    <div className="text-sm">
      <div
        className={`${ROW_GRID} border-b border-gray-200 px-2 pb-1 text-[11px] font-semibold uppercase tracking-wide text-gray-500`}
      >
        <span>Field</span>
        <span className="text-right">Legacy</span>
        <span className="text-right">New</span>
        <span className="text-right">Δ</span>
      </div>
      <dl className="divide-y divide-gray-100">
        {rows.map(row => (
          <div
            key={row.field}
            className={`${ROW_GRID} px-2 py-1.5 ${row.differs ? 'bg-accent-50' : ''}`}
          >
            <dt className={`truncate ${row.differs ? 'font-medium text-gray-900' : 'text-gray-600'}`}>
              {row.label}
            </dt>
            <dd className="text-right tabular-nums text-gray-800">{formatValue(row, row.legacy)}</dd>
            <dd className="text-right tabular-nums text-gray-800">{formatValue(row, row.next)}</dd>
            <dd
              className={`text-right tabular-nums ${
                row.differs ? 'font-semibold text-orange-700' : 'text-gray-400'
              }`}
            >
              {row.differs ? `${(row.relDiff * 100).toFixed(1)}%` : '—'}
            </dd>
          </div>
        ))}
      </dl>
    </div>
  );
}

function CopyBundleButton({ policy }: { policy: PolicyDiffGroup }) {
  const client = useSelector(selectClientDataOrNull);
  const partner = useSelector(selectMaybeClientPartner);
  const { copied, copy } = useClipboardCopy();

  if (!client) return null;

  return (
    <Button
      variant="outline"
      size="sm"
      onClick={() => copy(buildEngineDiffBundle({ client, partner, policy }))}
    >
      {copied ? <Check className="text-green-600" /> : <Copy />}
      {copied ? 'Copied' : 'Copy debug bundle'}
    </Button>
  );
}

interface EngineDiffSlideOutContentProps {
  fundingSourceId?: string;
}

export function EngineDiffSlideOutContent({ fundingSourceId }: EngineDiffSlideOutContentProps) {
  const diff = useEngineDiff(fundingSourceId);

  if (!diff) {
    return <p className="text-sm text-gray-500">No client loaded.</p>;
  }

  return (
    <div className="space-y-4">
      <p className="text-sm text-gray-600">
        Legacy phase-based coverage vs. the new month-based claim-projection engine, run on this
        client{fundingSourceId ? ' (showing the selected product only)' : ''}. Rows differing by more
        than 1% are highlighted. Unported products fall back to legacy in both runs and show no
        difference.
      </p>

      {!diff.anyDiffer && (
        <div className="rounded-md bg-green-50 px-3 py-2 text-sm text-green-800">
          Engines agree on all compared fields.
        </div>
      )}

      {diff.policies.map(policy => (
        <section key={policy.fundingSourceId} className="space-y-2">
          <div className="flex items-center justify-between gap-3">
            <h3 className="text-sm font-semibold text-gray-900">{policy.label}</h3>
            <CopyBundleButton policy={policy} />
          </div>
          <DiffRows rows={policy.scalarRows} />
          {policy.phaseGroups.length > 0 && (
            <CollapsibleSection title="Per-phase detail" defaultOpen={false}>
              <div className="space-y-4">
                {policy.phaseGroups.map((group, i) => (
                  <div key={`${group.label}-${i}`} className="space-y-1">
                    <h4 className="text-[11px] font-semibold uppercase tracking-wide text-gray-700">
                      {group.label}
                    </h4>
                    <DiffRows rows={group.rows} />
                  </div>
                ))}
              </div>
            </CollapsibleSection>
          )}
        </section>
      ))}

      {diff.combinedRows.length > 0 && (
        <section className="space-y-2">
          <h3 className="text-sm font-semibold text-gray-900">Combined</h3>
          <DiffRows rows={diff.combinedRows} />
        </section>
      )}
    </div>
  );
}
